import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import SyncIcon from '@mui/icons-material/Sync'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { authStore } from '@/store/authStore'
import { tokens } from '@/theme'
import { campanhaService } from '@/services/campanhaService'
import { documentoService } from '@/services/documentoService'
import { codigoErroDe, mensagemErroDe } from '@/components/pipeline/erros'
import { permissoesGestaoComercial } from '@/utils/gestaoComercial'
import { CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import {
  AUDIENCIA_ROTULO,
  Campanha,
  CampanhaRequest,
  PERIODICIDADE_ROTULO,
  PeriodicidadeMeta,
  StatusCampanha,
  TipoMeta,
  mensagemErroCampanha,
} from '@/types/campanha'
import { CampanhaDetalheDrawer } from './CampanhaDetalheDrawer'
import { AudienciaCampos, AudienciaValor } from './AudienciaCampos'

const PRODUTOS_OPCOES = [
  'Antecipacao de Recebiveis',
  'Capital de Giro',
  'Credito Rotativo',
]

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const formatDate = (iso: string) => {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

const STATUS_COLOR: Record<StatusCampanha, 'default' | 'success' | 'warning'> = {
  RASCUNHO: 'default',
  ATIVA: 'success',
  ENCERRADA: 'warning',
}

/** Arte da campanha: só imagem (o módulo de documentos aceita PNG/JPEG). */
const TIPOS_ARTE = ['image/png', 'image/jpeg']
const PREMIACAO_MAX = 2000

const emptyForm: CampanhaRequest = {
  nome: '',
  descricao: '',
  valorMeta: null,
  dataInicio: new Date().toISOString().slice(0, 10),
  dataFim: new Date().toISOString().slice(0, 10),
  produtoNome: '',
  status: 'RASCUNHO',
  periodicidade: 'PERIODO',
  tipoMeta: 'VOLUME',
  valorMetaParticipante: null,
  premiacao: '',
  arteDocumentoId: null,
  audienceType: 'TODOS_ATIVOS',
  audienciaCanais: [],
  audienciaEmpresas: [],
}

/** Rótulo curto da audiência para a coluna da tabela (sem contar quem ela alcança). */
const rotuloAudiencia = (c: Campanha): string => {
  if (!c.audienceType) return '—'
  if (c.audienceType === 'CANAIS' && c.audienciaCanais?.length) {
    return `${AUDIENCIA_ROTULO.CANAIS}: ${c.audienciaCanais
      .map((x) => CANAL_LABEL[x as CanalComercial] ?? x)
      .join(', ')}`
  }
  if (c.audienceType === 'EMPRESAS' && c.audienciaEmpresas?.length) {
    return `${AUDIENCIA_ROTULO.EMPRESAS} (${c.audienciaEmpresas.length})`
  }
  return AUDIENCIA_ROTULO[c.audienceType]
}

const CampanhasPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  // AN-8 (Q-AN8-2, R1): CRUD/status/excluir/audiência só ADMIN; participantes e produção do
  // ADMIN e do GERENTE com GESTOR_EQUIPE (só a equipe). GERENTE sem GESTOR e OPERADOR leem.
  const perm = useMemo(() => permissoesGestaoComercial(usuario?.permissoes), [usuario])
  const canEdit = perm.editaCampanha

  const [rows, setRows] = useState<Campanha[]>([])
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const [filtroStatus, setFiltroStatus] = useState<StatusCampanha | ''>('')

  const [dialogOpen, setDialogOpen] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [editando, setEditando] = useState<Campanha | null>(null)
  const [form, setForm] = useState<CampanhaRequest>(emptyForm)
  /** Encerrada é só leitura em metas e datas (o backend responde 422 CAMPANHA_ENCERRADA_IMUTAVEL). */
  const encerradaEmEdicao = editando?.status === 'ENCERRADA'
  const [detalhe, setDetalhe] = useState<Campanha | null>(null)
  /** Arte escolhida no diálogo (ainda não enviada) e a prévia local dela. */
  const [arte, setArte] = useState<File | null>(null)
  const [previaArte, setPreviaArte] = useState<string | null>(null)

  useEffect(() => {
    if (!arte) {
      setPreviaArte(null)
      return
    }
    const url = URL.createObjectURL(arte)
    setPreviaArte(url)
    return () => URL.revokeObjectURL(url)
  }, [arte])

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    setSemPermissao(false)
    try {
      const page = await campanhaService.listar(0, 100, filtroStatus || undefined)
      setRows(page.content ?? [])
    } catch (e) {
      // Consultor (ou papel sem leitura) recebe 403: estado de sem permissão, não erro genérico.
      if ((e as { status?: number })?.status === 403) setSemPermissao(true)
      else setErro(mensagemErroDe(e, 'Falha ao carregar campanhas'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [filtroStatus])

  useEffect(() => {
    carregar()
  }, [carregar])

  // Volume e receita nunca se somam (AN-7, Q25): o KPI de produção cita só as campanhas de
  // VOLUME; a receita de cada campanha RECEITA aparece na própria linha.
  const rowsVolume = useMemo(() => rows.filter((c) => (c.tipoMeta ?? 'VOLUME') === 'VOLUME'), [rows])
  const producaoTotal = useMemo(
    () => rowsVolume.reduce((acc, c) => acc + (c.producaoGanha || 0), 0),
    [rowsVolume]
  )
  // AN-8 (Q31): a meta coletiva é opcional — o antigo KPI "Atingimento" (Σ produção ÷ Σ metas,
  // calculado na tela) deixou de fazer sentido e saiu; o atingimento de cada campanha vem
  // pronto do backend na própria linha. No lugar: quantas campanhas listadas não têm meta.
  const semMetaColetiva = useMemo(() => rows.filter((c) => c.valorMeta == null).length, [rows])

  const sincronizar = async () => {
    setSincronizando(true)
    try {
      const r = await campanhaService.sincronizarAudiencias()
      enqueueSnackbar(
        `Audiências sincronizadas: ${r.campanhas} campanha(s), ${r.inscritos} inscrição(ões) nova(s)`,
        { variant: 'success' }
      )
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Falha ao sincronizar as audiências'), { variant: 'error' })
    } finally {
      setSincronizando(false)
    }
  }

  const abrirNova = () => {
    setEditando(null)
    setForm(emptyForm)
    setArte(null)
    setDialogOpen(true)
  }

  const abrirEdicao = (c: Campanha) => {
    setEditando(c)
    setForm({
      nome: c.nome,
      descricao: c.descricao ?? '',
      valorMeta: c.valorMeta ?? null,
      dataInicio: c.dataInicio,
      dataFim: c.dataFim,
      produtoNome: c.produtoNome ?? '',
      status: c.status,
      periodicidade: c.periodicidade ?? 'PERIODO',
      tipoMeta: c.tipoMeta ?? 'VOLUME',
      valorMetaParticipante: c.valorMetaParticipante ?? null,
      premiacao: c.premiacao ?? '',
      // PUT substitui tudo: sem isto, editar a campanha apagaria a arte já enviada.
      arteDocumentoId: c.arteDocumentoId ?? null,
      // PUT substitui tudo: a audiência gravada volta no formulário (backend antigo → geral).
      audienceType: c.audienceType ?? 'TODOS_ATIVOS',
      audienciaCanais: (c.audienciaCanais ?? []) as CanalComercial[],
      audienciaEmpresas: c.audienciaEmpresas ?? [],
    })
    setArte(null)
    setDialogOpen(true)
  }

  const audiencia: AudienciaValor = {
    audienceType: form.audienceType,
    audienciaCanais: form.audienciaCanais ?? [],
    audienciaEmpresas: form.audienciaEmpresas ?? [],
  }

  const escolherArte = (e: ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0] ?? null
    e.target.value = ''
    if (!arquivo) return
    if (!TIPOS_ARTE.includes(arquivo.type)) {
      enqueueSnackbar('A arte deve ser uma imagem PNG ou JPG', { variant: 'warning' })
      return
    }
    setArte(arquivo)
  }

  const fechar = () => {
    setDialogOpen(false)
    setEditando(null)
    setArte(null)
  }

  const salvar = async () => {
    if (!form.nome.trim()) {
      enqueueSnackbar('Nome é obrigatório', { variant: 'warning' })
      return
    }
    // Q31: meta coletiva opcional; se vier, positiva (400 CAMPANHA_META_INVALIDA).
    if (form.valorMeta != null && form.valorMeta <= 0) {
      enqueueSnackbar('A meta coletiva deve ser positiva (ou fique vazia)', { variant: 'warning' })
      return
    }
    if (form.dataFim < form.dataInicio) {
      enqueueSnackbar('Data fim não pode ser anterior à data início', { variant: 'warning' })
      return
    }
    if (form.valorMetaParticipante != null && form.valorMetaParticipante <= 0) {
      enqueueSnackbar('Meta por consultor deve ser positiva', { variant: 'warning' })
      return
    }
    // Mesma regra do backend (400 CAMPANHA_SEM_OBJETIVO), avisada antes de enviar.
    if (
      form.valorMeta == null &&
      form.valorMetaParticipante == null &&
      !form.descricao?.trim() &&
      !form.premiacao?.trim()
    ) {
      enqueueSnackbar('Informe ao menos uma meta, a descrição ou a premiação', { variant: 'warning' })
      return
    }
    if (form.audienceType === 'CANAIS' && !(form.audienciaCanais?.length)) {
      enqueueSnackbar('Escolha ao menos um canal para a audiência', { variant: 'warning' })
      return
    }
    if (form.audienceType === 'EMPRESAS' && !(form.audienciaEmpresas?.length)) {
      enqueueSnackbar('Escolha ao menos uma empresa para a audiência', { variant: 'warning' })
      return
    }
    setSalvando(true)
    try {
      // `consultorId` nunca vai (descontinuado: 400 CAMPANHA_CONSULTOR_ID_DESCONTINUADO).
      const payload: CampanhaRequest = {
        ...form,
        descricao: form.descricao?.trim() || null,
        produtoNome: form.produtoNome?.trim() || null,
        premiacao: form.premiacao?.trim() || null,
        valorMeta: form.valorMeta ?? null,
        valorMetaParticipante: form.valorMetaParticipante || null,
        tipoMeta: form.tipoMeta ?? 'VOLUME',
        audienciaCanais: form.audienceType === 'CANAIS' ? (form.audienciaCanais ?? []) : [],
        audienciaEmpresas: form.audienceType === 'EMPRESAS' ? (form.audienciaEmpresas ?? []) : [],
      }
      // Ordem obrigatória: a campanha existe ANTES da arte (o upload exige a entidade de
      // referência) → upload como documento CAMPANHA → a campanha guarda o id da arte.
      const salva = editando
        ? await campanhaService.atualizar(editando.id, payload)
        : await campanhaService.criar(payload)
      if (arte) {
        try {
          const doc = await documentoService.upload(arte, 'OUTRO', salva.id, 'CAMPANHA')
          await campanhaService.atualizar(salva.id, { ...payload, arteDocumentoId: doc.id })
        } catch (erroArte) {
          // A campanha fica salva; só a arte falhou — o usuário precisa saber o motivo. O `api`
          // rejeita com { message, status, data: { errorCode } } (não um Error): os helpers do
          // projeto leem esse envelope.
          enqueueSnackbar(
            codigoErroDe(erroArte) === 'DOCUMENTO_DUPLICADO'
              ? 'Campanha salva, mas esta imagem já está em uso em outra campanha; envie outro arquivo'
              : `Campanha salva, mas a arte não foi enviada: ${mensagemErroDe(erroArte, 'erro desconhecido')}`,
            { variant: 'warning' }
          )
        }
      }
      enqueueSnackbar(editando ? 'Campanha atualizada' : 'Campanha criada', { variant: 'success' })
      fechar()
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Falha ao salvar'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const alterarStatus = async (c: Campanha, novo: StatusCampanha) => {
    try {
      await campanhaService.alterarStatus(c.id, novo)
      enqueueSnackbar(`Campanha ${novo === 'ATIVA' ? 'ativada' : 'encerrada'}`, { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Falha ao alterar status'), { variant: 'error' })
    }
  }

  const remover = async (c: Campanha) => {
    if (!window.confirm(`Remover a campanha "${c.nome}"?`)) return
    try {
      await campanhaService.deletar(c.id)
      enqueueSnackbar('Campanha removida', { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Falha ao remover'), { variant: 'error' })
    }
  }

  return (
    <Box>
      <PageHeader
        title="Campanhas"
        subtitle="Metas comerciais com progresso baseado em reservas contratadas no período"
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={carregar} disabled={loading}>
              Atualizar
            </Button>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={sincronizando ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={() => void sincronizar()}
                disabled={sincronizando}
              >
                Sincronizar audiências
              </Button>
            )}
            {canEdit && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
                Nova campanha
              </Button>
            )}
          </Stack>
        }
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {semPermissao ? (
        <Paper>
          <EstadoDeLista estado="semPermissao" minHeight={240} />
        </Paper>
      ) : (
      <>
      {/* KPIs do Figma 28:448 — os quatro citam a MESMA lista da tabela abaixo.
          Produção e Participantes vêm dos participantes (V43, A14). Sem KPI de atingimento
          agregado: meta coletiva é opcional (AN-8, Q31) e a tela não calcula razão. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Ativas"
            carregando={loading}
            valor={rows.filter((c) => c.status === 'ATIVA').length}
            legenda="Campanhas ativas no filtro atual"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Produção"
            carregando={loading}
            valor={formatBRL(producaoTotal)}
            legenda="Produção Ganha dos participantes, no período (campanhas de volume)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Participantes"
            carregando={loading}
            valor={rows.reduce((acc, c) => acc + (c.participantes || 0), 0)}
            legenda="Inscrições nas campanhas listadas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Sem meta coletiva"
            carregando={loading}
            valor={semMetaColetiva}
            legenda="Campanhas listadas só com meta individual, descrição ou premiação"
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Status"
            select
            size="small"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value as StatusCampanha | '')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="RASCUNHO">Rascunho</MenuItem>
            <MenuItem value="ATIVA">Ativa</MenuItem>
            <MenuItem value="ENCERRADA">Encerrada</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>NOME</strong></TableCell>
                <TableCell><strong>VIGÊNCIA</strong></TableCell>
                <TableCell><strong>PERÍODO</strong></TableCell>
                <TableCell><strong>PRODUTO</strong></TableCell>
                <TableCell><strong>AUDIÊNCIA</strong></TableCell>
                <TableCell><strong>META</strong></TableCell>
                <TableCell><strong>PRODUÇÃO</strong></TableCell>
                <TableCell sx={{ minWidth: 180 }}><strong>ATINGIMENTO</strong></TableCell>
                <TableCell><strong>STATUS</strong></TableCell>
                <TableCell align="right"><strong>AÇÕES</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma campanha cadastrada.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {c.nome}
                      </Typography>
                      {c.descricao && (
                        <Typography variant="caption" color="text.secondary">
                          {c.descricao}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(c.dataInicio)} → {formatDate(c.dataFim)}
                    </TableCell>
                    <TableCell>
                      {/* Janela corrente vem pronta do backend (JanelaMeta) — a tela não calcula. */}
                      <Typography variant="body2">
                        {c.rotuloPeriodo ?? PERIODICIDADE_ROTULO[c.periodicidade ?? 'PERIODO']}
                      </Typography>
                      {c.rotuloPeriodo && (
                        <Typography variant="caption" color="text.secondary">
                          {PERIODICIDADE_ROTULO[c.periodicidade ?? 'PERIODO']}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{c.produtoNome ?? 'Todos'}</TableCell>
                    <TableCell>
                      {/* Só o tipo e os critérios — nunca quantos consultores a audiência alcança. */}
                      <Typography variant="body2">{rotuloAudiencia(c)}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {/* Q31: sem meta coletiva → "—", nunca R$ 0,00. */}
                      {c.valorMeta != null ? formatBRL(c.valorMeta) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: tokens.statusSuccess, fontWeight: 600 }}>
                      {formatBRL(c.producaoGanha ?? 0)}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {c.tipoMeta === 'RECEITA'
                          ? `Receita confirmada · ${c.oportunidadesContratadas ?? 0} lançamento(s)`
                          : `Volume ganho · ${c.oportunidadesContratadas ?? 0} contratada(s)`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {c.atingimentoPercent != null && c.valorMeta != null ? (
                        <Stack spacing={0.5}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, c.atingimentoPercent)}
                            sx={{ height: 8, borderRadius: 4 }}
                            color={c.atingimentoPercent >= 100 ? 'success' : 'primary'}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {c.atingimentoPercent}% · {c.participantes ?? 0} participante(s)
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sem meta coletiva · {c.participantes ?? 0} participante(s)
                        </Typography>
                      )}
                      {c.participantesEquipe != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {c.participantesEquipe} da sua equipe
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={c.status} size="small" color={STATUS_COLOR[c.status]} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="text" onClick={() => setDetalhe(c)}>
                        Abrir →
                      </Button>
                      {canEdit && c.status === 'RASCUNHO' && (
                        <Tooltip title="Ativar">
                          <IconButton size="small" aria-label="Ativar" onClick={() => alterarStatus(c, 'ATIVA')}>
                            <PlayArrowIcon fontSize="small" color="success" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit && c.status === 'ATIVA' && (
                        <Tooltip title="Encerrar">
                          <IconButton size="small" aria-label="Encerrar" onClick={() => alterarStatus(c, 'ENCERRADA')}>
                            <StopIcon fontSize="small" color="warning" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEdicao(c)}>
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip title="Remover">
                          <IconButton size="small" onClick={() => remover(c)}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </>
      )}

      {/* ── DRAWER: Regra · Participantes · Ranking · Histórico (A14; AN-8) ── */}
      <CampanhaDetalheDrawer
        campanha={detalhe ? (rows.find((r) => r.id === detalhe.id) ?? detalhe) : null}
        permissoes={perm}
        onClose={() => setDetalhe(null)}
        onAlterou={() => void carregar()}
      />

      {/* ── MODAL: Criar/editar campanha ── */}
      <Dialog open={dialogOpen} onClose={fechar} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
          {editando ? 'Editar campanha' : 'Nova campanha'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {encerradaEmEdicao && (
              <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
                Campanha encerrada: metas e datas são só leitura
              </Alert>
            )}
            <TextField
              label="Nome"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Descrição"
              value={form.descricao ?? ''}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Periodicidade"
                  select
                  value={form.periodicidade ?? 'PERIODO'}
                  onChange={(e) => setForm({ ...form, periodicidade: e.target.value as PeriodicidadeMeta })}
                  fullWidth
                  helperText="Mensal e anual apuram o mês/ano civil dentro da vigência"
                >
                  <MenuItem value="PERIODO">Vigência inteira</MenuItem>
                  <MenuItem value="MENSAL">Mensal</MenuItem>
                  <MenuItem value="ANUAL">Anual</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Tipo de meta"
                  select
                  value={form.tipoMeta ?? 'VOLUME'}
                  onChange={(e) => setForm({ ...form, tipoMeta: e.target.value as TipoMeta })}
                  // O tipo só muda em rascunho (Q-AN7-7): fora dele o backend responde 422.
                  disabled={!!editando && editando.status !== 'RASCUNHO'}
                  fullWidth
                  helperText={
                    !!editando && editando.status !== 'RASCUNHO'
                      ? 'O tipo de meta só muda enquanto a campanha está em rascunho'
                      : (form.tipoMeta ?? 'VOLUME') === 'RECEITA'
                        ? 'Receita Originei confirmada pelo Financeiro, por competência'
                        : 'Valor estimado das reservas ganhas, pela data de assinatura'
                  }
                >
                  <MenuItem value="VOLUME">Volume ganho</MenuItem>
                  <MenuItem value="RECEITA">Receita Originei confirmada</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Meta coletiva"
                  type="number"
                  value={form.valorMeta ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, valorMeta: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  disabled={encerradaEmEdicao}
                  fullWidth
                  helperText="Opcional — vazia = campanha sem meta coletiva"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Meta por consultor (padrão)"
                  type="number"
                  value={form.valorMetaParticipante ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      valorMetaParticipante: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  disabled={encerradaEmEdicao}
                  fullWidth
                  helperText="Opcional — cada participante pode ter a sua"
                />
              </Grid>
            </Grid>
            <TextField
              label="Premiação"
              value={form.premiacao ?? ''}
              onChange={(e) => setForm({ ...form, premiacao: e.target.value })}
              multiline
              rows={2}
              fullWidth
              inputProps={{ maxLength: PREMIACAO_MAX }}
              helperText="Informativa — não gera pagamento"
            />
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" component="label" size="small">
                  {arte || form.arteDocumentoId ? 'Trocar arte' : 'Arte da campanha (PNG/JPG)'}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg"
                    aria-label="Arte da campanha"
                    onChange={escolherArte}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {arte ? arte.name : form.arteDocumentoId ? 'Arte já enviada' : 'Sem arte'}
                </Typography>
              </Stack>
              {previaArte && (
                <Box
                  component="img"
                  src={previaArte}
                  alt="Prévia da arte da campanha"
                  sx={{ mt: 1, maxHeight: 120, maxWidth: '100%', borderRadius: 1 }}
                />
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Data início"
                  type="date"
                  value={form.dataInicio}
                  onChange={(e) => setForm({ ...form, dataInicio: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  disabled={encerradaEmEdicao}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Data fim"
                  type="date"
                  value={form.dataFim}
                  onChange={(e) => setForm({ ...form, dataFim: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  disabled={encerradaEmEdicao}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <AudienciaCampos
              valor={audiencia}
              onChange={(v) => setForm({ ...form, ...v })}
              statusAtual={editando?.status}
              tipoAtual={editando?.audienceType}
            />
            <TextField
              label="Produto (opcional)"
              select
              value={form.produtoNome ?? ''}
              onChange={(e) => setForm({ ...form, produtoNome: e.target.value })}
              fullWidth
              helperText="Vazio = qualquer produto contribui para a meta"
            >
              <MenuItem value="">Todos</MenuItem>
              {PRODUTOS_OPCOES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Status"
              select
              value={form.status ?? 'RASCUNHO'}
              onChange={(e) => setForm({ ...form, status: e.target.value as StatusCampanha })}
              fullWidth
            >
              {/* Campanha ativada/encerrada não volta a rascunho (o backend responde 422). */}
              <MenuItem value="RASCUNHO" disabled={!!editando && editando.status !== 'RASCUNHO'}>
                Rascunho
              </MenuItem>
              <MenuItem value="ATIVA">Ativa</MenuItem>
              <MenuItem value="ENCERRADA">Encerrada</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvar}
            disabled={salvando}
            startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CampanhasPage
